import update from "immutability-helper";
import produce from "immer";

import { getNewItems, createCommitReduce } from "./case_logic/createActions";
import {
  moveChecklistItemReduce,
  toggleChecklistItemReduce,
} from "./case_logic/updateActions";
import { updateAndOrQueue } from "shared/middleware/waitForId/waitForIdSlice";

const ADD_CHECKLIST_ITEM = "ADD_CHECKLIST_ITEM";
const CREATE_CHECKLIST_ITEM = "CREATE_CHECKLIST_ITEM";
const CREATE_CHECKLIST_ITEM_COMMIT = "CREATE_CHECKLIST_ITEM_COMMIT";

const FETCH_CHECKLIST_ITEMS = "FETCH_CHECKLIST_ITEMS";
const FETCH_CHECKLIST_ITEMS_COMMIT = "FETCH_CHECKLIST_ITEMS_COMMIT";
const INCREMENT_LOCAL_ID_INCREMENTOR = "INCREMENT_LOCAL_ID_INCREMENTOR";

const UPDATE_CHECKLIST_ITEM_NAME = "UPDATE_CHECKLIST_ITEM_NAME";
const UPDATE_CHECKLIST_ITEM_COMMIT = "UPDATE_CHECKLIST_ITEM_COMMIT";
const MOVE_CHECKLIST_ITEM = "MOVE_CHECKLIST_ITEM";
const MOVE_CHECKLIST_ITEM_COMMIT = "MOVE_CHECKLIST_ITEM_COMMIT";
const TOGGLE_CHECKLIST_ITEM = "TOGGLE_CHECKLIST_ITEM";
const TOGGLE_CHECKLIST_ITEM_COMMIT = "TOGGLE_CHECKLIST_ITEM_COMMIT";

const MERGE_CHECKLIST_ITEM_WITH_ABOVE = "MERGE_CHECKLIST_ITEM_WITH_ABOVE";
const REMOVE_CHECKLIST_ITEM = "REMOVE_CHECKLIST_ITEM";
const REMOVE_CHECKLIST_ITEM_COMMIT = "REMOVE_CHECKLIST_ITEM_COMMIT";

//READ FUNCTIONS

function isPending(localId, state) {
  return !!state.checklist.pendingIds[localId];
}

// ACTION CREATORS
// CREATE actions
function createChecklistItem(params) {
  const body = {
    name: params.name,
    item_id: params.itemId,
    position: params.index,
  };
  return {
    type: CREATE_CHECKLIST_ITEM,
    payload: params,
    meta: {
      offline: {
        effect: { url: "checklist_items", method: "POST", json: body },
        commit: {
          type: CREATE_CHECKLIST_ITEM_COMMIT,
          meta: { localId: params.localId },
        },
      },
    },
  };
}

function addChecklistItem(params) {
  return (dispatch, getState) => {
    const newLocalId = `unsaved-checklist-item-${
      getState().checklist.localIdIncrementor
    }`;
    dispatch(incrementLocalIdIncrementor());
    dispatch({
      type: ADD_CHECKLIST_ITEM,
      payload: { ...params, ...{ newLocalId } },
    });
  };
}

function incrementLocalIdIncrementor() {
  return {
    type: INCREMENT_LOCAL_ID_INCREMENTOR,
  };
}

// READ actions
function fetchChecklistItems(params) {
  return {
    type: FETCH_CHECKLIST_ITEMS,
    payload: params,
    meta: {
      offline: {
        effect: { url: `checklists/${params.itemId}`, method: "GET" },
        commit: { type: FETCH_CHECKLIST_ITEMS_COMMIT },
      },
    },
  };
}

// UPDATE actions
function updateChecklistItemName({ id, name }) {
  return (dispatch) => {
    const effect = {
      url: "checklist_items",
      id: id,
      json: { name },
      method: "PATCH",
    };
    const updateAction = {
      type: UPDATE_CHECKLIST_ITEM_NAME,
      payload: { id, name },
      meta: {
        offline: {
          effect: effect,
          commit: { type: UPDATE_CHECKLIST_ITEM_COMMIT },
        },
      },
    };
    updateAndOrQueue({
      dependsOn: CREATE_CHECKLIST_ITEM_COMMIT,
      ...{ id, effect, updateAction, dispatch },
    });
  };
}

function moveChecklistItem({ oldIndex, newIndex, id }) {
  return (dispatch) => {
    const effect = {
      url: "checklist_items",
      id: id,
      json: { position: newIndex },
      method: "PATCH",
    };
    const updateAction = {
      type: MOVE_CHECKLIST_ITEM,
      payload: { oldIndex, newIndex },
      meta: {
        offline: {
          effect: effect,
          commit: { type: MOVE_CHECKLIST_ITEM_COMMIT },
        },
      },
    };
    updateAndOrQueue({
      dependsOn: CREATE_CHECKLIST_ITEM_COMMIT,
      ...{ id, effect, updateAction, dispatch },
    });
  };
}

function toggleChecklistItem({ id, done, name, index }) {
  return (dispatch) => {
    const effect = {
      url: "checklist_items",
      id: id,
      json: { done, name },
      method: "PATCH",
    };
    const updateAction = {
      type: TOGGLE_CHECKLIST_ITEM,
      payload: { id, done, name, index },
      meta: {
        offline: {
          effect: effect,
          commit: { type: TOGGLE_CHECKLIST_ITEM_COMMIT },
        },
      },
    };
    updateAndOrQueue({
      dependsOn: CREATE_CHECKLIST_ITEM_COMMIT,
      ...{ id, effect, updateAction, dispatch },
    });
  };
}

// DELETE actions
function removeChecklistItem({ index, id, isUnsaved }) {
  return (dispatch, getState) => {
    if (isUnsaved && !isPending(id, getState())) {
      return dispatch({
        type: REMOVE_CHECKLIST_ITEM,
        payload: { index, id, isUnsaved },
      });
    }
    const effect = {
      url: "checklist_items",
      id: id,
      json: { active: false },
      method: "PATCH",
    };
    const updateAction = {
      type: REMOVE_CHECKLIST_ITEM,
      payload: { index, id, isUnsaved },
      meta: {
        offline: {
          effect: effect,
          commit: { type: REMOVE_CHECKLIST_ITEM_COMMIT },
        },
      },
    };
    updateAndOrQueue({
      dependsOn: CREATE_CHECKLIST_ITEM_COMMIT,
      ...{ id, effect, updateAction, dispatch },
    });
  };
}

function mergeChecklistItemWithAbove({ index, id, isUnsaved, name }) {
  return (dispatch, getState) => {
    const { entities, undoneIds } = getState().checklist.openChecklist;
    if (index > 0) {
      const aboveId = undoneIds[index - 1];
      const aboveItem = entities[aboveId];
      const aboveName = aboveItem.name;
      const newName = aboveName + name;
      dispatch(updateChecklistItemName({ id: aboveId, name: newName }));
    }
    dispatch(removeChecklistItem({ id, index, isUnsaved }));
  };
}

const initialState = {
  openChecklist: {
    undoneIds: [],
    doneIds: [],
    entities: {},
  },
  pendingIds: {},
  status: "idle",
  localIdIncrementor: 1,
};

function makeEntities(arr) {
  return arr.reduce((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {});
}

export default function checklistReducer(state = initialState, action) {
  try {
    switch (action.type) {
      // CREATE actions
      case ADD_CHECKLIST_ITEM: {
        const { undoneIds, entities } = state.openChecklist;
        let { name1, name2, index, newLocalId } = action.payload;

        if (index === "end") {
          index = undoneIds.length;
        }
        const { newIds, newEntities } = getNewItems({
          name1,
          name2,
          index,
          undoneIds,
          entities,
          newLocalId,
        });
        return update(state, {
          openChecklist: {
            entities: { $set: { ...entities, ...newEntities } },
            undoneIds: { $splice: [[index, 1, ...newIds]] },
          },
        });
      }

      case CREATE_CHECKLIST_ITEM: {
        return produce(state, (draftState) => {
          draftState.status = "pending";
          draftState.pendingIds[action.payload.localId] = true;
          draftState.openChecklist.entities[action.payload.localId].name =
            action.payload.name;
        });
      }

      case CREATE_CHECKLIST_ITEM_COMMIT: {
        return createCommitReduce({ action, state });
      }

      case INCREMENT_LOCAL_ID_INCREMENTOR: {
        return update(state, {
          localIdIncrementor: { $set: state.localIdIncrementor + 1 },
        });
      }

      // READ actions
      case FETCH_CHECKLIST_ITEMS: {
        return update(state, {
          status: { $set: "pending" },
          openChecklist: {
            entities: { $set: {} },
            undoneIds: { $set: [] },
            doneIds: { $set: [] },
          },
        });
      }

      case FETCH_CHECKLIST_ITEMS_COMMIT: {
        const { undone_items, done_items } = action.payload;

        const entities = makeEntities(undone_items.concat(done_items));
        const undoneIds = undone_items.map((item) => item.id);
        const doneIds = done_items.map((item) => item.id);

        return update(state, {
          openChecklist: {
            entities: { $set: entities },
            undoneIds: { $set: undoneIds },
            doneIds: { $set: doneIds },
          },
          status: { $set: "idle" },
        });
      }
      // UPDATE ACTIONS

      case UPDATE_CHECKLIST_ITEM_NAME: {
        return update(state, {
          status: { $set: "pending" },
          openChecklist: {
            entities: {
              [action.payload.id]: {
                $merge: { name: action.payload.name },
              },
            },
          },
        });
      }

      case UPDATE_CHECKLIST_ITEM_COMMIT: {
        return update(state, { status: { $set: "idle" } });
      }

      case MOVE_CHECKLIST_ITEM: {
        return moveChecklistItemReduce({ action, state });
      }

      case MOVE_CHECKLIST_ITEM_COMMIT: {
        return update(state, { status: { $set: "idle" } });
      }

      case TOGGLE_CHECKLIST_ITEM: {
        return toggleChecklistItemReduce({ action, state });
      }

      case TOGGLE_CHECKLIST_ITEM_COMMIT: {
        return update(state, { status: { $set: "idle" } });
      }

      case REMOVE_CHECKLIST_ITEM: {
        const { id, index } = action.payload;
        return update(state, {
          status: { $set: "pending" },
          openChecklist: {
            entities: { $unset: [id] },
            undoneIds: { $splice: [[index, 1]] },
          },
        });
      }

      case REMOVE_CHECKLIST_ITEM_COMMIT: {
        return update(state, { status: { $set: "idle" } });
      }
      default:
        return state;
    }
  } catch (error) {
    console.log("Error in reducer action " + action.type, error);
  }
}

export {
  isPending,
  addChecklistItem,
  createChecklistItem,
  fetchChecklistItems,
  incrementLocalIdIncrementor,
  updateChecklistItemName,
  moveChecklistItem,
  toggleChecklistItem,
  mergeChecklistItemWithAbove,
  removeChecklistItem,
};
